import { storeToRefs } from "pinia";
import { useRegisterStore } from "vue-app/pages/PageRegister/PageRegister.store";
import { validateEmail, validatePhoneNumber, validateUrl } from "vue-app/utils/validators";
import { amplitudeSendEvent } from "vue-app/utils/amplitude";

export const validateForm = (value?: "name" | "email" | "phoneNumber" | "shopUrl") => {
  const translation = window._trans._register.form.validation;
  const store = useRegisterStore();
  const {
    formValidationErrors,
    formValidationErrorsBackend,
    name,
    phoneNumber,
    email,
    shopUrl,
    captcha,
    selectedPlatform,
  } = storeToRefs(store);

  formValidationErrors.value.isValidating = true;

  const diacriticsRegex = /[ąćęłńóśźżĄĆĘŁŃÓŚŹŻãáàâçéêíôõúüÃÁÀÂÇÉÊÍÔÕÚÜ]/;

  const getNameError = () => {
    const isNameValid = /^[\p{L}\s]+$/u.test(name.value); // Unicode letters and spaces only
    if (name.value.trim().length < 2) {
      formValidationErrors.value.name = translation.name.minChars;
    } else if (name.value.trim().length > 50) {
      formValidationErrors.value.name = translation.name.maxChars;
    } else if (!isNameValid) {
      formValidationErrors.value.name = translation.name.notValid;
    } else {
      formValidationErrors.value.name = "";
    }

    if (formValidationErrors.value.name) {
      amplitudeSendEvent("register_form_field_validation_error", {
        platform: String(selectedPlatform.value),
        field: "name",
        step: "form",
        source: "frontend",
        name_error: formValidationErrors.value.name,
      });
    } else {
      amplitudeSendEvent("register_form_field_validation_success", {
        platform: String(selectedPlatform.value),
        field: "name",
        step: "form",
        source: "frontend",
        field_filled: true,
      });
    }
  };

  const getEmailError = () => {
    if (email.value.trim().length < 6) {
      formValidationErrors.value.email = translation.email.minChars;
    } else if (email.value.trim().split("@")[0].length > 64) {
      formValidationErrors.value.email = translation.email.maxChars;
    } else if (diacriticsRegex.test(email.value)) {
      formValidationErrors.value.email = translation.email.onlyLatin;
    } else if (!validateEmail(email.value)) {
      formValidationErrors.value.email = translation.invalid.email;
    } else if (email.value.includes(" ") || email.value.includes(",")) {
      formValidationErrors.value.email = translation.email.noSpacesCommas;
    } else {
      formValidationErrors.value.email = "";
    }

    if (formValidationErrors.value.email) {
      amplitudeSendEvent("register_form_field_validation_error", {
        platform: String(selectedPlatform.value),
        field: "email",
        step: "form",
        source: "frontend",
        email_error: formValidationErrors.value.email,
      });
    } else {
      amplitudeSendEvent("register_form_field_validation_success", {
        platform: String(selectedPlatform.value),
        field: "email",
        step: "form",
        source: "frontend",
        field_filled: true,
      });
    }
  };

  const getPhoneNumberError = () => {
    if (phoneNumber.value.trim().length < 9) {
      formValidationErrors.value.phoneNumber = translation.phone.minChars;
    } else if (phoneNumber.value.trim().length > 20) {
      formValidationErrors.value.phoneNumber = translation.phone.maxChars;
    } else if (!validatePhoneNumber(phoneNumber.value)) {
      formValidationErrors.value.phoneNumber = translation.invalid.phone;
    } else {
      formValidationErrors.value.phoneNumber = "";
    }

    if (formValidationErrors.value.phoneNumber) {
      amplitudeSendEvent("register_form_field_validation_error", {
        platform: String(selectedPlatform.value),
        field: "phoneNumber",
        step: "form",
        source: "frontend",
        phone_error: formValidationErrors.value.phoneNumber,
      });
    } else {
      amplitudeSendEvent("register_form_field_validation_success", {
        platform: String(selectedPlatform.value),
        field: "phoneNumber",
        step: "form",
        source: "frontend",
        field_filled: true,
      });
    }
  };

  const getShopUrlError = () => {
    if (shopUrl.value.trim().length < 3) {
      formValidationErrors.value.shopUrl = translation.shopUrl.minChars;
    } else if (shopUrl.value.trim().length > 50) {
      formValidationErrors.value.shopUrl = translation.shopUrl.maxChars;
    } else if (diacriticsRegex.test(shopUrl.value)) {
      formValidationErrors.value.shopUrl = translation.shopUrl.onlyLatin;
    } else if (!validateUrl({ url: shopUrl.value })) {
      formValidationErrors.value.shopUrl = translation.invalid.url;
    } else {
      formValidationErrors.value.shopUrl = "";
    }

    if (formValidationErrors.value.shopUrl) {
      amplitudeSendEvent("register_form_field_validation_error", {
        platform: String(selectedPlatform.value),
        field: "shopUrl",
        step: "form",
        source: "frontend",
        url_error: formValidationErrors.value.shopUrl,
      });
    } else {
      amplitudeSendEvent("register_form_field_validation_success", {
        platform: String(selectedPlatform.value),
        field: "shopUrl",
        step: "form",
        source: "frontend",
        field_filled: true,
      });
    }
  };

  const getCaptchaError = () => {
    if (!captcha.value) {
      formValidationErrors.value.captcha = translation.required;
    } else {
      formValidationErrors.value.captcha = "";
      formValidationErrorsBackend.value.captcha = "";
    }
  };

  switch (value) {
    case "name":
      getNameError();
      break;
    case "email":
      getEmailError();
      break;
    case "phoneNumber":
      getPhoneNumberError();
      break;
    case "shopUrl":
      getShopUrlError();
      break;
    default:
      getNameError();
      getEmailError();
      getPhoneNumberError();
      getShopUrlError();
      getCaptchaError();
      break;
  }

  const hasErrors = !!(
    formValidationErrors.value.name ||
    formValidationErrors.value.email ||
    formValidationErrors.value.phoneNumber ||
    formValidationErrors.value.shopUrl ||
    formValidationErrors.value.captcha
  );

  // Track full form validation when validating all fields
  if (!value && hasErrors) {
    const errorDetails: Record<string, string> = {};

    if (formValidationErrors.value.name) errorDetails.name_error = formValidationErrors.value.name;
    if (formValidationErrors.value.email) errorDetails.email_error = formValidationErrors.value.email;
    if (formValidationErrors.value.phoneNumber) errorDetails.phone_error = formValidationErrors.value.phoneNumber;
    if (formValidationErrors.value.shopUrl) errorDetails.url_error = formValidationErrors.value.shopUrl;
    if (formValidationErrors.value.captcha) errorDetails.captcha_error = formValidationErrors.value.captcha;

    amplitudeSendEvent("register_form_field_validation_error", {
      platform: String(selectedPlatform.value),
      field: "all",
      step: "form",
      source: "frontend",
      ...errorDetails,
    });
  } else if (!value && !hasErrors) {
    amplitudeSendEvent("register_form_validation_success", {
      platform: String(selectedPlatform.value),
      step: "form",
      source: "frontend",
      name_filled: !!name.value.trim(),
      email_filled: !!email.value.trim(),
      phone_filled: !!phoneNumber.value.trim(),
      url_filled: !!shopUrl.value.trim(),
    });
  }
};

/**
 * This function splits a string at the first space, e.g. splitAtFirstSpace("John Doe") return ["John", "Doe"].
 * Check more examples in test scenarios in RegisterForm.helpers.test.ts.
 */
export const splitAtFirstSpace = (value: string) => {
  const valueTrimmed = value.trim();
  if (!valueTrimmed) return [];
  const i = valueTrimmed.indexOf(" ");
  if (i > 0) {
    return [valueTrimmed.substring(0, i), valueTrimmed.substring(i + 1)];
  } else return [valueTrimmed];
};
